import { useState } from 'react';
import { amount } from './HabitHistory';

/**
 * Recording that a habit happened.
 *
 * One control, used by both screens that offer logging, because they had drifted into disagreeing
 * about what pressing the button means. The day view asked for an amount and (since the typical
 * value landed) offered a one-press shortcut. The habits screen — the screen *about* habits —
 * rendered a bare "+1" for every habit regardless of its unit and called `logHabit(id)` with no
 * value at all, so the server's default of 1 applied. Pressing it on a habit measured in
 * kilometres recorded **one kilometre**; on one measured in pages, one page. There was no amount
 * field on that screen to correct it with.
 *
 * That is worse than the friction it appeared to avoid. A form that asks an awkward question gets
 * skipped; a button that silently records the wrong number gets pressed, and every history,
 * met-rate, drift verdict and target suggestion downstream is computed from the result.
 *
 * The rules, in one place now:
 *
 * - **Unitless habit** → a plain "Log" that records one. An occurrence has no amount, and asking
 *   for one would be a form where a button belongs.
 * - **Measured habit with history** → one press for the amount usually logged, plus a field for
 *   the day that is not usual.
 * - **Measured habit with no history** → the field alone. The first entry is the one thing the
 *   app has no opinion about, so it does not invent one.
 * - **Measurement habit** → the field alone, always, and worded as a reading.
 *
 * That last rule is the one this phase adds. `typicalAmount` withholds the shortcut from
 * *negative* habits, which covers a weight you are trying to get under and covers nothing else:
 * a "VO2max, at least 45" habit is positive, is a measurement, and was offered a **+45** button
 * whose plus sign is a lie about what pressing it does. The mode of your recent readings is not
 * a reading you are about to take, either — a body weight is different every morning, which is
 * the entire reason it is being tracked.
 *
 * - **Tallied ceiling** ("added sugar, at most 1 serving a week") → **clean**, which logs `0`.
 *   For these the honest answer most days is none, and none was the one number the row would not
 *   take: the field refused `0` (`parsed > 0`) while the API accepts it (`min(0)`), so even the
 *   keyboard could not say it (`01a08096-e031`). A logged zero is evidence and judges the period
 *   held — `habitAggregation.spec.ts` pins that, since this button is worthless without it.
 *
 *   Not on an **abstinence** habit (at most 0): the breach query counts entry rows and never reads
 *   the value, so a `0 drinks` entry would break the run it meant to keep. A clean day there needs
 *   no entry at all — the account showing use anywhere is the evidence. Not on a **read** ceiling
 *   either: "log 0 mmHg" is the misreading `isReading` exists to prevent. Both facts come from the
 *   service (`isAbstinence`, `isReading`) rather than from `aggregation`, which the health nut
 *   stores as `sum` on six of its readings — keyed on that, Systolic BP would get a clean button.
 */
export interface LoggableHabit {
  id: string;
  name: string;
  unit: string | null;
  polarity: string;
  /** The amount usually logged, or null before this habit has ever been logged. */
  typicalValue: number | null;
  /**
   * How the habit's figure is read. A reading is recorded, not added — so no "+", and no
   * shortcut offering to record last week's number as today's.
   */
  aggregation: string;
  /** A ceiling you read rather than tally — said by the service. Withholds `clean`. */
  isReading: boolean;
  /** At most zero — said by the service. Withholds `clean`, since an entry would breach it. */
  isAbstinence: boolean;
}

export function LogHabit({
  habit,
  onLog,
  pending,
  onUndo,
  undoPending = false,
}: {
  habit: LoggableHabit;
  onLog: (value: number) => void;
  pending: boolean;
  /**
   * Taking back the day's last entry, or absent when there is nothing to take back — so the
   * control is never a button that does nothing.
   */
  onUndo?: () => void;
  undoPending?: boolean;
}) {
  // `typed`, not `amount`: `amount()` is where a habit's number and unit are spelled, and this
  // is the raw string in the box beside it.
  const [typed, setTyped] = useState('');

  // A habit measured in something — glasses, minutes, pages — needs the amount asked for.
  // A bare occurrence does not.
  const measured = habit.unit !== null && habit.unit.trim() !== '';
  /*
    Two different questions, deliberately kept apart. `isReading` here is the reduction — it words
    the field as a reading and withholds the `+typical` shortcut from a positive `latest` habit
    (VO2max), which the service's `isReading` does not cover because it only classifies ceilings.
    `talliedCeiling` is the service's answer, because it is the one that has to be right about
    ceilings stored as `sum`.
  */
  const isReading = habit.aggregation !== 'sum';
  const talliedCeiling = habit.polarity === 'negative' && !habit.isAbstinence && !habit.isReading;
  const parsed = Number(typed);
  // Zero is an answer only where "none" is one: on a tallied ceiling. Anywhere else a 0 is either
  // nothing (a count), nonsense (a reading) or a breach (abstinence).
  const canSubmit =
    typed.trim() !== '' && Number.isFinite(parsed) && (talliedCeiling ? parsed >= 0 : parsed > 0);

  /*
    One box, laid out by one rule (`.habit-log` in styles.css), on every screen that mounts this.

    This returned three bare siblings — or, for an unmeasured habit, one bare button — into
    whichever flex line the parent happened to draw. So the parent owned the layout, there were two
    parents, and the unit word's width set where the button landed: down Today's 31 rows at 375px
    the button's left edge ran from 20 to 277.3 (`01a08096-dd72`). Habits had already been repaired
    for the same mechanism in its own class (`.habit-actions`, the 40rem rule) and Today never got
    it, which is the argument for the box being this component's rather than either parent's: the
    next surface to mount `LogHabit` gets the grid without having to rediscover the defect.

    Four fixed tracks — quick-log, amount, undo, Log — with every branch placing its pieces in the
    same ones, so an unmeasured row's button lands where a measured row's does. The undo sits
    *before* Log rather than after it because it comes and goes: as the last track its arrival
    would shove Log left on exactly the rows that had just been logged.
  */
  const undo = onUndo && (
    <button
      type="button"
      className="link habit-log-undo"
      disabled={undoPending}
      aria-label={`Undo the last ${habit.name} entry`}
      onClick={onUndo}
    >
      undo
    </button>
  );

  /*
    One verb. The button said `+1` or `Log one` on an unmeasured habit (by polarity), `Record` on a
    reading and `Log` otherwise — four words for one act, and four widths, which is what kept the
    last track from being the same width on every row. A reading is carried by the field's label
    and the unit beside it; the accessible name already said "Log" in every branch.
  */
  if (!measured) {
    return (
      <div className="habit-log">
        {undo}
        <button
          type="button"
          className="habit-log-commit"
          disabled={pending}
          aria-label={`Log ${habit.name}`}
          onClick={() => onLog(1)}
        >
          Log
        </button>
      </div>
    );
  }

  return (
    <div className="habit-log">
      {/* No shortcut for a reading — see the note above the interface. */}
      {talliedCeiling ? (
        <button
          type="button"
          className="quick-log"
          disabled={pending}
          aria-label={`Log ${amount(0, habit.unit)} of ${habit.name}`}
          title={`None — logs ${amount(0, habit.unit)}`}
          onClick={() => onLog(0)}
        >
          clean
        </button>
      ) : (
        habit.typicalValue !== null &&
        !isReading && (
          <button
            type="button"
            className="quick-log"
            disabled={pending}
            /* Through `amount()`, like the row this button sits on: the unit is stored plural
             because that is how you name a thing you do several of a week, so a typical value of
             one read "+1 sessions" beside a row now saying "1 session of 4". */
            aria-label={`Log ${amount(habit.typicalValue, habit.unit)} of ${habit.name}`}
            title="The amount you usually log"
            onClick={() => onLog(habit.typicalValue as number)}
          >
            +{amount(habit.typicalValue, habit.unit)}
          </button>
        )
      )}
      {/*
        The unit stands beside the field, not inside it.

        It was the placeholder, which fails twice: it truncates in a 4.5rem box — "servings" showed
        as "serv", so two habits with different units looked identical while logging — and it
        disappears the moment you type, which is exactly when you want to know what you are
        counting. As a suffix it is always there and never competes with the value.

        `aria-hidden`, because the field's own label already names the unit and a screen reader
        should not hear it twice. That is also what lets it truncate: the full unit is in the
        field's label and in the `title`, so an ellipsis loses nothing a reader cannot get back.
      */}
      <span className="amount-field">
        <input
          type="number"
          min={0}
          step="any"
          className="habit-amount"
          value={typed}
          aria-label={
            isReading
              ? `Reading for ${habit.name} in ${habit.unit}`
              : `Amount of ${habit.name} in ${habit.unit}`
          }
          onChange={(e) => setTyped(e.target.value)}
        />
        {habit.unit && (
          <span className="amount-unit" aria-hidden="true" title={habit.unit}>
            {habit.unit}
          </span>
        )}
      </span>
      {undo}
      <button
        type="button"
        className="habit-log-commit"
        disabled={!canSubmit || pending}
        onClick={() => {
          onLog(parsed);
          setTyped('');
        }}
      >
        Log
      </button>
    </div>
  );
}
